import { stringify } from 'query-string';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { HttpClient } from 'api/http-client';
import { BulkActionPayload } from 'api/models/core-types';
import { ProjectPreview, TaskPreview } from 'api/notifications';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import {
  RejectNoteType,
  Timesheet,
  TimesheetReminder,
  TimesheetStatus,
} from 'components/organisms/timesheets/types';
import {
  ApiFilter,
  ApiGroupFilter,
} from 'utils/simple-search-filter/simple-search-filter';

export enum ApiTimesheetStatus {
  NotSubmitted = 'NOT_SUBMITTED',
  Submitted = 'SUBMITTED',
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
}

export enum TimesheetEntryType {
  TaskEntry = 'TASK_ENTRY',
  AdhocEntry = 'ADHOC_ENTRY',
  Integration = 'INTEGRATION_ENTRY',
  ProjectEntry = 'PROJECT_ENTRY',
  PhaseEntry = 'PHASE_ENTRY',
}

export enum TimesheetCalendarSourceType {
  GoogleCalendar = 'GOOGLE_CALENDAR',
  OutlookCalendar = 'OUTLOOK_CALENDAR',
}

export enum TimesheetUISourceType {
  Project = 'PROJECT',
  Task = 'TASK',
  Phase = 'PHASE',
  Adhoc = 'ADHOC',
}
export type TimesheetSourceType =
  | TimesheetUISourceType
  | TimesheetCalendarSourceType;
export interface TimesheetCategory {
  categoryId: number;
  categoryName: string;
}

export enum SourcePage {
  MyTimeSheet = 'MY_TIME_SHEET',
  Approvals = 'APPROVALS',
  TaskPane = 'TIMEENTRY_TASK_PANE',
}

export interface TimesheetTimeEntryRequest {
  timeEntryId: number;
  date: string;
  notes?: string;
  categoryId?: number;
  billable: boolean;
  minutes: number;
  userId: number;
  sourcePage: SourcePage;
}
export interface TimesheetTaskEntryRequest extends TimesheetTimeEntryRequest {
  entryType: TimesheetEntryType.TaskEntry;
  taskId: number;
  sourceType: TimesheetSourceType;
  sourceId: number;
}

export interface TimesheetPhaseEntryRequest extends TimesheetTimeEntryRequest {
  entryType: TimesheetEntryType.PhaseEntry;
  phaseId: number;
  sourceType: TimesheetSourceType;
  sourceId: number;
}

export interface TimesheetAdhocEntryRequest extends ApiTimesheetTimeEntry {
  entryType: TimesheetEntryType.AdhocEntry;
  activityName: string;
  projectId?: number;
}

export interface TimesheetCalendarEntryRequest extends ApiTimesheetTimeEntry {
  entryType: TimesheetEntryType.Integration;
  activityName: string;
  projectId: number;
  sourceId: string;
  sourceType: TimesheetSourceType;
}

export type TimesheetEntryRequest =
  | TimesheetTaskEntryRequest
  | TimesheetAdhocEntryRequest
  | TimesheetCalendarEntryRequest
  | TimesheetPhaseEntryRequest;

export interface TimesheetTaskParamBase {
  startDate: string;
  endDate: string;
}

export interface TimesheetTaskParam extends TimesheetTaskParamBase {
  sourceId: number;
  sourceType: TimesheetSourceType;
}
export interface TimesheetAdhocTaskParam extends TimesheetTaskParamBase {
  activityName: string | undefined;
  projectId: number | undefined;
}

export interface ApiTimesheetReminder {
  createdBy: number;
  createdAt: number;
}

export interface ApiTimesheetTimeEntry {
  timeEntryId: number;
  date: string;
  notes?: string;
  category?: TimesheetCategory;
  billable: boolean;
  minutes: number;
  userId: number;
}
export interface ApiTimesheetTaskEntry extends ApiTimesheetTimeEntry {
  type: TimesheetEntryType.TaskEntry;
  task: TaskPreview;
}

export interface ApiTimesheetAdhocEntry extends ApiTimesheetTimeEntry {
  type: TimesheetEntryType.AdhocEntry;
  activityName: string;
  project?: ProjectPreview;
}

export type ApiTimesheetEntry = ApiTimesheetTaskEntry | ApiTimesheetAdhocEntry;
export interface ApiUserTimesheet {
  entries: ApiTimesheetEntry[];
  reminders: ApiTimesheetReminder[];
  status: ApiTimesheetStatus;
  userId: number;
  startDate: string;
  updatedAt: number;
}

export const getEquivalentStatusForApiStatus = (
  apiStatus: ApiTimesheetStatus,
): TimesheetStatus => {
  switch (apiStatus) {
    case ApiTimesheetStatus.Approved:
      return TimesheetStatus.Approved;
    case ApiTimesheetStatus.Submitted:
      return TimesheetStatus.Submitted;
    case ApiTimesheetStatus.NotSubmitted:
      return TimesheetStatus.NotSubmitted;
    case ApiTimesheetStatus.Rejected:
      return TimesheetStatus.Rejected;
    default:
      return TimesheetStatus.NotSubmitted;
  }
};

const asUserTimesheet = (userTimesheets: ApiUserTimesheet[]): Timesheet[] => {
  return userTimesheets.map((userTimesheet) => {
    return {
      ...userTimesheet,
      status: getEquivalentStatusForApiStatus(userTimesheet.status),
    } as Timesheet;
  });
};

type AddTimeEntryPayload = Omit<TimesheetEntryRequest, 'timeEntryId'>;
class TimesheetHttpClient extends HttpClient {
  public getTimesheetForUser = async (
    userId: number,
    startDate: string,
    projectId?: number,
  ): Promise<Timesheet> =>
    this.get<ApiUserTimesheet[]>(
      projectId
        ? `/users/${userId}/timesheets/${startDate}?projectId=${projectId}`
        : `/users/${userId}/timesheets/${startDate}`,
    ).then((timesheets) => asUserTimesheet(timesheets)[0]);

  public getTimesheets = async (
    startDate: string,
    filter?: GroupedFilter,
  ): Promise<Timesheet[]> => {
    const transformedFilterString = filter
      ? ApiGroupFilter.toQueryStringFormatForGroups(filter.groupCriteria)
      : '';

    return this.get<ApiUserTimesheet[]>(
      `/timesheets/${startDate}/filter?${transformedFilterString}`,
    ).then((timesheets) => asUserTimesheet(timesheets));
  };

  public addTimeEntry = async (
    userId: number,
    timeEntry: AddTimeEntryPayload,
  ): Promise<ApiTimesheetEntry> =>
    this.post(`/users/${userId}/time-entries`, timeEntry);

  public bulkAddTimeEntry = async (
    userId: number,
    timeEntry: BulkActionPayload<AddTimeEntryPayload>,
  ): Promise<{
    timeEntries: Array<{
      status: boolean;
      timeEntryResponse: ApiTimesheetEntry;
    }>;
  }> => this.post(`/users/${userId}/time-entries/bulk-action`, timeEntry);

  public editAdhocTask = async (userId: number, timeEntries: any) =>
    this.post(`/users/${userId}/time-entries/bulk-action`, timeEntries);

  public updateTimeEntry = async (
    timeEntryId: number,
    values: Partial<TimesheetEntryRequest>,
  ): Promise<ApiTimesheetEntry> =>
    this.put(`/time-entries/${timeEntryId}`, values);

  public deleteTimeEntry = async (timeEntryId: number): Promise<null> =>
    this.delete(`/time-entries/${timeEntryId}`);

  public deleteTimesheetTask = async (timeEntryIds: string): Promise<null> =>
    this.delete(`/time-entries?${timeEntryIds}`);

  public searchTimeEntries = async (
    filter: SimpleSearchFilter,
  ): Promise<ApiTimesheetEntry[]> => {
    const fieldsAsQueryString = [
      ...(filter.nativeFields ?? []).map(
        ApiFilter.nativeFieldToQueryStringFormat,
      ),
      ...(filter.customFields ?? []).map(
        ApiFilter.customFieldToQueryStringFormat,
      ),
    ];

    return this.get(
      `/time-entries/search?${fieldsAsQueryString.join('&')}&${stringify(
        {
          match: fieldsAsQueryString.length > 0 ? filter.match : null,
        },
        { skipNull: true },
      )}`,
    );
  };

  public getAllCategories = async (): Promise<TimesheetCategory[]> =>
    this.get<TimesheetCategory[]>('/timesheets/categories');

  public approveTimesheet = async (
    userId: number,
    startDate: string,
    updatedAt: number,
    projectId?: number,
    timeEntryIds?: number[],
  ): Promise<Timesheet> =>
    this.put<
      ApiUserTimesheet[],
      {
        status: ApiTimesheetStatus;
        timeEntryIds?: number[];
        projectId?: number;
      }
    >(`/users/${userId}/timesheets/${startDate}?lastUpdatedAt=${updatedAt}`, {
      status: ApiTimesheetStatus.Approved,
      timeEntryIds,
      projectId,
    }).then((timesheets) => asUserTimesheet(timesheets)[0]);

  public approveAllTimesheets = async (
    startDate: string,
    extraFilter: string,
    filter?: GroupedFilter,
    searchValue?: string,
  ): Promise<Timesheet[]> => {
    const transformedFilterString = filter
      ? ApiGroupFilter.toQueryStringFormatForGroups(filter.groupCriteria)
      : '';

    return this.put<
      ApiUserTimesheet[],
      {
        filter: string;
        search?: string;
      }
    >(`/timesheets/${startDate}/approve-all?${transformedFilterString}`, {
      filter: extraFilter,
      search: searchValue,
    }).then((timesheets) => asUserTimesheet(timesheets));
  };

  public submitTimesheet = async (
    userId: number,
    startDate: string,
    projectId?: number,
    timeEntryIds?: number[],
  ): Promise<Timesheet> =>
    this.put<
      ApiUserTimesheet[],
      {
        status: ApiTimesheetStatus;
        projectId?: number;
        timeEntryIds?: number[];
      }
    >(`/users/${userId}/timesheets/${startDate}`, {
      status: ApiTimesheetStatus.Submitted,
      projectId,
      timeEntryIds,
    }).then((timesheets) => asUserTimesheet(timesheets)[0]);

  public rejectTimesheet = async (
    userId: number,
    startDate: string,
    updatedAt: number,
    content?: string,
    projectId?: number,
    timeEntryIds?: number[],
  ): Promise<Timesheet> =>
    this.put<
      ApiUserTimesheet[],
      {
        status: ApiTimesheetStatus;
        rejectNote?: string;
        projectId?: number;
        timeEntryIds?: number[];
      }
    >(`/users/${userId}/timesheets/${startDate}?lastUpdatedAt=${updatedAt}`, {
      status: ApiTimesheetStatus.Rejected,
      rejectNote: content,
      timeEntryIds,
      projectId,
    }).then((timesheets) => asUserTimesheet(timesheets)[0]);

  public getActivitiesOfUserInProject = async (
    userId: number,
    projectId: number | 'all' | undefined,
  ): Promise<string[]> =>
    projectId
      ? this.get(
          `/users/${userId}/time-entries/activities?projectId=${projectId}`,
        )
      : this.get(`/users/${userId}/time-entries/activities`);

  public withdrawTimesheet = async (
    userId: number,
    startDate: string,
    projectId?: number,
    timeEntryIds?: number[],
  ): Promise<Timesheet> =>
    this.put<
      ApiUserTimesheet[],
      {
        status: ApiTimesheetStatus;
        timeEntryIds?: number[];
        projectId?: number;
      }
    >(`/users/${userId}/timesheets/${startDate}`, {
      status: ApiTimesheetStatus.Submitted,
      timeEntryIds,
      projectId,
    }).then((timesheets) => asUserTimesheet(timesheets)[0]);

  public unsubmitTimesheet = async (
    userId: number,
    startDate: string,
    timeEntryIds?: number[],
  ): Promise<Timesheet> =>
    this.put<
      ApiUserTimesheet[],
      { status: ApiTimesheetStatus; timeEntryIds?: number[] }
    >(`/users/${userId}/timesheets/${startDate}`, {
      status: ApiTimesheetStatus.NotSubmitted,
      timeEntryIds,
    }).then((timesheets) => asUserTimesheet(timesheets)[0]);

  public sendReminder = async (
    userId: number,
    startDate: string,
    projectId?: number,
  ): Promise<TimesheetReminder> =>
    this.post(
      projectId
        ? `/users/${userId}/timesheets/${startDate}/reminders?projectId=${projectId}`
        : `/users/${userId}/timesheets/${startDate}/reminders`,
    );

  public sendBulkReminder = async (
    startDate: string,
    extraFilter: string,
    filter?: GroupedFilter,
    searchValue?: string,
  ): Promise<number[]> => {
    const transformedFilterString = filter
      ? ApiGroupFilter.toQueryStringFormatForGroups(filter.groupCriteria)
      : '';

    return this.post<
      number[],
      {
        filter: string;
        search?: string;
      }
    >(`/users/timesheets/${startDate}/remind-all?${transformedFilterString}`, {
      filter: extraFilter,
      search: searchValue,
    });
  };

  public getRejectNote = async (
    userId: number,
    startDate: string,
  ): Promise<RejectNoteType[]> =>
    this.get(`/users/${userId}/timesheets/${startDate}/note`);
}

export const TimesheetAPI = new TimesheetHttpClient();
